import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Fade,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  Add,
  ChatOutlined,
  HistoryOutlined,
  PersonOutlined,
  FeedbackOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  AccessTimeOutlined,
  AutoAwesome,
  TrendingUpOutlined,
  DescriptionOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchSummaries } from '../store/slices/summariesSlice';
import { fetchProcessStats } from '../store/slices/processesSlice';
import { safeFormatDate } from '../utils/dateUtils';
import EnhancedMarkdownRenderer from '../components/EnhancedMarkdownRenderer';

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useAppSelector((state) => state.auth.user);
  const summaries = useAppSelector((state: any) => state.summaries.summaries);
  const loading = useAppSelector((state: any) => state.summaries.loading);
  const processStats = useAppSelector((state: any) => state.processes.stats);

  // Check if user has admin privileges (admin, anis-admin, superadmin)
  const adminRoles = ['admin', 'anis-admin', 'superadmin'];
  const isAdmin = user?.role ? adminRoles.includes(user.role) : false;

  // Pagination state for summaries
  const [displayedSummariesCount, setDisplayedSummariesCount] = useState(3);
  const SUMMARIES_PER_PAGE = 3;

  useEffect(() => {
    dispatch(fetchSummaries());
    if (isAdmin) {
      dispatch(fetchProcessStats());
    }
  }, [dispatch, user, isAdmin]);

  const recentSummaries = summaries.slice(0, displayedSummariesCount);
  const hasMoreSummaries = summaries.length > displayedSummariesCount;

  const handleShowMoreSummaries = () => {
    setDisplayedSummariesCount(prev => Math.min(prev + SUMMARIES_PER_PAGE, summaries.length));
  };

  // Function to truncate markdown content to a specific character limit
  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    
    // Find the last complete word within the limit
    const truncated = content.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    return lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) + '...' : truncated + '...';
  };
  
  // For admin users, summaries are already filtered by backend to current user
  // For regular users, show all visible summaries
  // Calculate statistics using process data only
  const totalSummaries = processStats?.total_processes || 0;
  const completedSummaries = processStats?.completed || 0;
  const pendingSummaries = processStats?.processing || 0; // Only processing status for pending
  const successRate = totalSummaries > 0 ? Math.round((completedSummaries / totalSummaries) * 100) : 0;

  // Define quick actions based on user role
  const quickActions = isAdmin ? [
    {
      title: 'Generate Summary',
      description: 'Create new summaries',
      icon: <Add />,
      path: '/summaries',
      color: '#a31830',
    },
    {
      title: 'Chat with AI',
      description: 'Ask questions about your data',
      icon: <ChatOutlined />,
      path: '/chatbot',
      color: '#28a745',
    },
    {
      title: 'Feedback',
      description: 'Share your feedback with us',
      icon: <FeedbackOutlined />,
      path: '/feedback',
      color: '#ff9800',
    },
    {
      title: 'View History',
      description: 'View your processing history',
      icon: <HistoryOutlined />,
      path: '/processes',
      color: '#6c757d',
    },
    {
      title: 'Profile Settings',
      description: 'Manage your account',
      icon: <PersonOutlined />,
      path: '/profile',
      color: '#6f42c1',
    },
  ] : [
    {
      title: 'View Summaries',
      description: 'Browse available summaries',
      icon: <DescriptionOutlined />,
      path: '/summaries',
      color: '#a31830',
    },
    {
      title: 'Chat with AI',
      description: 'Ask questions about your data',
      icon: <ChatOutlined />,
      path: '/chatbot',
      color: '#28a745',
    },
    {
      title: 'Profile Settings',
      description: 'Manage your account',
      icon: <PersonOutlined />,
      path: '/profile',
      color: '#6f42c1',
    },
    {
      title: 'Feedback',
      description: 'Share your feedback with us',
      icon: <FeedbackOutlined />,
      path: '/feedback',
      color: '#ff9800',
    },
  ];
  return (
    <Container sx={{ 
      py: isMobile ? 2 : 4,
      px: isMobile ? 2 : 3,
      maxWidth: '100%',
      width: '100%',
      }}>
        {/* Welcome Section */}
        <Fade in timeout={600}>
          <Paper 
            elevation={0}
            sx={{ 
              mb: isMobile ? 3 : 4, 
              p: isMobile ? 3 : 4,
              borderRadius: isMobile ? '8px' : '12px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  mb: 2,
                  color: '#1f2937',
                  fontSize: isMobile ? '1.5rem' : '1.8rem',
                }}
              >
                Welcome back, {user?.username}!
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#6b7280', 
                  fontWeight: 400, 
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  lineHeight: 1.6,
                }}
              >
                Here's what's happening with your email summaries today.
              </Typography>
            </Box>
          </Paper>
        </Fade>

        {/* Stats Cards - Only visible for admin users */}
        {isAdmin && (
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 4 : 5 }}>
            {[
              {
                title: 'Total Summaries',
                value: totalSummaries,
                icon: <BarChartOutlined />,
                iconColor: '#dc2626',
                delay: 200
              },
              {
                title: 'Completed',
                value: completedSummaries,
                icon: <CheckCircleOutlined />,
                iconColor: '#16a34a',
                delay: 400
              },
              {
                title: 'Pending',
                value: pendingSummaries,
                icon: <AccessTimeOutlined />,
                iconColor: '#ea580c',
                delay: 600
              },
              {
                title: 'Success Rate',
                value: `${successRate}%`,
                icon: <TrendingUpOutlined />,
                iconColor: '#2563eb',
                delay: 800
              }
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                <Fade in timeout={1000}>
                  <Card
                       sx={{
                         backgroundColor: '#ffffff',
                         borderRadius: isMobile ? '8px' : '12px',
                         transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                         border: '1px solid #e5e7eb',
                         boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                         '&:hover': {
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                         }
                       }}
                     >
                       <CardContent sx={{ py: isMobile ? 2.5 : 3, px: isMobile ? 2.5 : 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#6b7280', mb: 1, fontWeight: 500 }}>
                            {stat.title}
                          </Typography>
                          <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 500, color: '#1f2937', fontSize: isMobile ? '1.5rem' : '1.75rem' }}>
                            {stat.value}
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%', 
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                          }}
                        >
                          {React.cloneElement(stat.icon, { sx: { fontSize: 24, color: stat.iconColor || '#718096' } })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Quick Actions */}
        <Fade in timeout={1200}>
          <Box sx={{ mb: isMobile ? 4 : 5 }}>
            <Typography 
              variant={isMobile ? "h6" : "h4"} 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 500, 
                mb: isMobile ? 2 : 4, 
                color: '#1f2937',
                textAlign: 'center',
                fontSize: isMobile ? '1.25rem' : undefined,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '4px',
                  backgroundColor: '#a31830',
                  borderRadius: '2px',
                }
              }}
            >
              Quick Actions
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
                  <Fade in timeout={1400}>
                    <Card
                      sx={{
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          borderColor: '#a31830',
                          '& .action-icon': {
                            color: '#a31830',
                          },
                          '& .action-title': {
                            color: '#a31830',
                          }
                        },
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Box 
                          sx={{ 
                            mb: 3,
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            backgroundColor: '#f9fafb',
                            border: '2px solid #e5e7eb',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {React.cloneElement(action.icon, { 
                            className: 'action-icon',
                            sx: { 
                              fontSize: 28, 
                              color: '#6b7280',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            } 
                          })}
                        </Box>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          gutterBottom 
                          className="action-title"
                          sx={{ 
                            fontWeight: 500, 
                            color: '#1f2937',
                            mb: 2,
                            fontSize: isMobile ? '1rem' : '1.125rem',
                            transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {action.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6b7280',
                            lineHeight: 1.6,
                            fontSize: isMobile ? '0.8rem' : '0.95rem'
                          }}
                        >
                          {action.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>

        {/* Recent Summaries - Only visible for admin users */}
        {isAdmin && (
          <Fade in timeout={1600}>
            <Box>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 500, 
                  mb: 4, 
                  color: '#1f2937',
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60px',
                    height: '4px',
                    backgroundColor: '#a31830',
                    borderRadius: '2px',
                  }
                }}
              >
                Recent Summaries
              </Typography>
            
            {loading ? (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#1f2937', mb: 2 }}>
                    Loading your summaries...
                  </Typography>
                </Box>
                <LinearProgress 
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#f3f4f6',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#a31830',
                      borderRadius: 4,
                    }
                  }}
                />
              </Paper>
            ) : recentSummaries.length === 0 ? (
              <Paper 
                elevation={0}
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Box 
                    sx={{ 
                      mb: 3,
                      p: 3,
                      borderRadius: '50%',
                      backgroundColor: '#f9fafb',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 48, color: '#a31830' }} />
                  </Box>
                  <Typography variant="h5" sx={{ color: '#1f2937', fontWeight: 500, mb: 2 }}>
                    No summaries yet
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', mb: 4, maxWidth: '400px', mx: 'auto' }}>
                    Generate your first email summary to get started with AI-powered insights.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AutoAwesome />}
                    onClick={() => navigate('/summaries')}
                    sx={{
                      borderRadius: '8px',
                      py: 1.5,
                      px: 4,
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    Create First Summary
                  </Button>
                </CardContent>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {recentSummaries.map((summary: any, index: number) => (
                  <Grid item xs={12} md={6} lg={4} key={summary.id}>
                    <Fade in timeout={1800}>
                      <Card
                        sx={{
                          backgroundColor: '#ffffff',
                          cursor: 'pointer',
                          borderRadius: '12px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          height: '350px',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            borderColor: '#a31830',
                          },
                        }}
                        onClick={() => navigate(`/summaries?selected=${summary.id}`)}
                      >
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography 
                              variant="h6" 
                              component="h3" 
                              sx={{ 
                                fontWeight: 500, 
                                color: '#1f2937',
                                flex: 1,
                                mr: 2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {summary.title}
                            </Typography>
                            <Chip
                              label={summary.status}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                ...(summary.status === 'completed' && {
                                  backgroundColor: 'success.main',
                                  color: 'success.contrastText',
                                }),
                                ...(summary.status === 'pending' && {
                                  backgroundColor: 'warning.main',
                                  color: 'warning.contrastText',
                                }),
                                ...(summary.status === 'error' && {
                                  backgroundColor: 'error.main',
                                  color: 'error.contrastText',
                                }),
                              }}
                            />
                          </Box>
                          
                          <Box 
                            sx={{ 
                              color: '#6b7280', 
                              mb: 3,
                              lineHeight: 1.6,
                              flex: 1,
                              overflow: 'hidden',
                              '& > div': {
                                fontSize: '0.875rem'
                              }
                            }}
                          >
                            <EnhancedMarkdownRenderer 
                              content={truncateContent(summary.content, 150)} 
                              variant="dialog"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 500 }}>
                              {safeFormatDate(summary.created_at, 'MMM dd, yyyy')}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Fade>
        )}

        {/* Show More Summaries Button */}
        {isAdmin && hasMoreSummaries && (
          <Fade in timeout={1800}>
            <Box sx={{ textAlign: 'center', mt: 4, minHeight: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleShowMoreSummaries}
                sx={{
                  borderRadius: '12px',
                  py: 2,
                  px: 5,
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#ffffff',
                  backgroundColor: '#a31830',
                  border: '2px solid #a31830',
                  minHeight: '56px',
                  boxShadow: '0 4px 12px rgba(163, 24, 48, 0.3)',
                  '&:hover': {
                    backgroundColor: '#8b1426',
                    borderColor: '#8b1426',
                    boxShadow: '0 6px 16px rgba(163, 24, 48, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Show More Summaries ({summaries.length - displayedSummariesCount} remaining)
              </Button>
            </Box>
          </Fade>
        )}

        {isAdmin && recentSummaries.length > 0 && (
          <Fade in timeout={2000}>
            <Box sx={{ textAlign: 'center', mt: 4, minHeight: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/summaries')}
                sx={{
                  borderRadius: '8px',
                  py: 1.5,
                  px: 4,
                  fontWeight: 500,
                  textTransform: 'none',
                  border: '2px solid',
                  borderColor: '#a31830',
                  color: '#a31830',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  minHeight: '48px',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: '#a31830',
                    color: '#ffffff',
                    boxShadow: '0 4px 12px rgba(163, 24, 48, 0.25)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                View All Summaries
              </Button>
            </Box>
          </Fade>
        )}


      </Container>
  );
};

export default UserDashboard;
